import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authHeaders, Toast, getODataRows } from './listPageStyles';
import { StatusBadge } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/Licenses';
const MENU_API = '/api/Menu';

const EDITION_OPTIONS = [
  { value: 'Standard', label: 'Standard' },
  { value: 'Professional', label: 'Professional' },
  { value: 'Enterprise', label: 'Enterprise' },
];
const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const EMPTY_FORM = { name: '', edition: 'Standard', maxUsers: 10, durationDays: '', isActive: 'true' };

// SuperAdmin'e özel menüler bir lisans planında yer almamalı.
const SUPERADMIN_MENU_IDS = new Set([9900, 9901, 9902, 9903]);

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  return undefined;
}
function unwrap(payload) { return pick(payload, 'data', 'Data') ?? payload; }

const mId = (m) => Number(pick(m, 'id', 'Id'));
const mParent = (m) => { const v = pick(m, 'parentID', 'ParentID', 'parentId', 'ParentId'); return v == null ? null : Number(v); };
const mActive = (m) => (pick(m, 'isActive', 'IsActive') ?? true) !== false;
const mLabel = (m) => pick(m, 'title', 'Title', 'name', 'Name') ?? `#${mId(m)}`;
const mSort = (m) => Number(pick(m, 'sortOrder', 'SortOrder') ?? 0);

function buildMenuTree(flat) {
  const active = flat.filter((m) => mActive(m) && !SUPERADMIN_MENU_IDS.has(mId(m)));
  const childrenOf = (pid) => active
    .filter((m) => mParent(m) === pid)
    .sort((a, b) => mSort(a) - mSort(b))
    .map((n) => ({ id: mId(n), label: mLabel(n), children: childrenOf(mId(n)) }));
  return childrenOf(null);
}
function collectIds(node) { return [node.id, ...node.children.flatMap(collectIds)]; }

function rowToForm(r) {
  return {
    name: pick(r, 'name', 'Name') ?? '',
    edition: pick(r, 'edition', 'Edition') ?? 'Standard',
    maxUsers: pick(r, 'maxUsers', 'MaxUsers') ?? 10,
    durationDays: pick(r, 'durationDays', 'DurationDays') ?? '',
    isActive: (pick(r, 'isActive', 'IsActive') ?? true) ? 'true' : 'false',
  };
}

export function LicenseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete, canCopy } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Lisans' : 'Lisans Detayı', 'Lisans planı ve menü kapsamı.');

  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // Menü kapsamı
  const [menus, setMenus] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [menusLoading, setMenusLoading] = useState(false);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/GetById?id=${id}`, { headers: authHeaders() });
      const body = res.ok ? await res.json() : null;
      const r = unwrap(body);
      if (r && (r.id || r.Id)) {
        setForm(rowToForm(r));
        setAudit({ createdBy: pick(r, 'createdBy', 'CreatedBy', 'createdUser', 'CreatedUser'), createdDate: pick(r, 'createdDate', 'CreatedDate'), updatedBy: pick(r, 'updatedBy', 'UpdatedBy', 'modifiedUser', 'ModifiedUser'), updatedDate: pick(r, 'updatedDate', 'UpdatedDate', 'modifiedDate', 'ModifiedDate') });
        const ids = pick(r, 'menuIds', 'MenuIds') ?? [];
        setSelected(new Set((Array.isArray(ids) ? ids : []).map(Number)));
      } else {
        showToast('error', 'Lisans bulunamadı');
      }
    } catch {
      showToast('error', 'Lisans yüklenemedi');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // Menü ağacı
  useEffect(() => {
    setMenusLoading(true);
    fetch(`${MENU_API}?$orderby=SortOrder asc&$top=1000`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const rows = data ? getODataRows(data) : [];
        setMenus(rows);
        setExpanded(new Set(rows.filter((m) => mParent(m) == null).map(mId)));
      })
      .catch(() => setMenus([]))
      .finally(() => setMenusLoading(false));
  }, []);

  const menuTree = useMemo(() => buildMenuTree(menus), [menus]);
  const parentMap = useMemo(() => {
    const map = new Map();
    menus.forEach((m) => map.set(mId(m), mParent(m)));
    return map;
  }, [menus]);
  const withAncestors = useCallback((idSet) => {
    const out = new Set(idSet);
    idSet.forEach((startId) => { let p = parentMap.get(startId); while (p != null) { out.add(p); p = parentMap.get(p); } });
    return out;
  }, [parentMap]);

  const nodeState = useCallback((node) => {
    const ids = collectIds(node);
    const on = ids.filter((i) => selected.has(i)).length;
    if (on === 0) return 'off';
    return on === ids.length ? 'on' : 'partial';
  }, [selected]);

  const toggleNode = (node) => {
    const ids = collectIds(node);
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = ids.every((i) => next.has(i));
      if (allOn) ids.forEach((i) => next.delete(i)); else ids.forEach((i) => next.add(i));
      return next;
    });
  };
  const toggleExpand = (nodeId) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });
  const selectAll = () => setSelected(new Set(menus.filter((m) => mActive(m) && !SUPERADMIN_MENU_IDS.has(mId(m))).map(mId)));
  const clearAll = () => setSelected(new Set());

  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.name.trim()) { showToast('error', 'Plan adı zorunludur.'); return; }
    setSaving(true);
    try {
      const menuIds = [...withAncestors(selected)];
      const payload = {
        name: form.name.trim(),
        edition: form.edition,
        maxUsers: Number(form.maxUsers) || 0,
        durationDays: form.durationDays === '' ? null : Number(form.durationDays),
        isActive: form.isActive === 'true',
        menuIds,
      };
      const res = isNew
        ? await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) })
        : await fetch(`${API_BASE}/Update`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ ...payload, id: Number(id) }) });
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) throw new Error(pick(body, 'message', 'Message') || `Kayıt başarısız (${res.status})`);
      if (isNew && andNew) {
        showToast('success', 'Lisans eklendi. Yeni kayda devam edebilirsiniz.');
        setForm(EMPTY_FORM); setSelected(new Set()); setTried(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      showToast('success', isNew ? 'Lisans eklendi.' : 'Lisans güncellendi.');
      setTimeout(() => navigate('/licenses'), 600);
    } catch (e) {
      showToast('error', e.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: Number(id) }) });
      const body = await res.json().catch(() => null);
      if (!res.ok || pick(body, 'success', 'Success') === false) throw new Error(pick(body, 'message', 'Message') || `Silme başarısız (${res.status})`);
      showToast('success', 'Lisans silindi.');
      setTimeout(() => navigate('/licenses'), 400);
    } catch (e) {
      showToast('error', e.message || 'Silme başarısız');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const statusBadge = !isNew && (form.isActive === 'true'
    ? <StatusBadge tone="ok">Aktif</StatusBadge>
    : <StatusBadge tone="neutral">Pasif</StatusBadge>);

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/licenses')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Oluştur'} icon="check" onClick={handleSave} disabled={saving}
          menu={[{ label: 'Kaydet ve Yeni', desc: 'Kaydet, listeye dönme, yeni kayıt aç', icon: 'plus', onClick: () => persist({ andNew: true }) }]}
        />
      ) : (
        <button className="dps-btn primary" onClick={handleSave} disabled={saving}>
          <Icon name="check" size={17} /> {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      ))}
    </>
  );

  const renderNode = (node, depth = 0) => {
    const st = nodeState(node);
    const hasKids = node.children.length > 0;
    const isOpen = expanded.has(node.id);
    return (
      <div key={node.id}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', paddingLeft: depth * 22 }}>
          {hasKids ? (
            <button type="button" onClick={() => toggleExpand(node.id)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', padding: 2, color: 'inherit' }}
              aria-label={isOpen ? 'Kapat' : 'Aç'}>
              <Icon name={isOpen ? 'chevD' : 'chevR'} size={14} />
            </button>
          ) : <span style={{ width: 18, display: 'inline-block' }} />}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: hasKids ? 600 : 400 }}>
            <input
              type="checkbox"
              checked={st === 'on'}
              ref={(el) => { if (el) el.indeterminate = st === 'partial'; }}
              onChange={() => toggleNode(node)}
            />
            <span>{node.label}</span>
          </label>
        </div>
        {hasKids && isOpen && node.children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <DetailShell
      crumb={[{ label: 'Lisanslar', to: '/licenses' }, { label: isNew ? 'Yeni Lisans' : 'Lisans Detayı', current: true }]}
      status={statusBadge}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'License', entityId: id }}
      tabs={[
        { key: 'info', label: 'Plan Bilgileri', icon: 'shield' },
        { key: 'menus', label: 'Menü Kapsamı', icon: 'list' },
      ]}
      activeTab={tab}
      onTab={setTab}
      actions={actions}
      loading={loading}
    >
      {tab === 'info' && (
        <div className="dps-form">
          <Section icon="shield" title="Lisans Planı" subtitle="Edisyon, kullanıcı limiti ve geçerlilik süresi"
            note="Süre (gün) boş bırakılırsa lisans süresiz kabul edilir. Tenant'a atandığında bitiş tarihi bu süreden hesaplanır.">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="name" label="Plan Adı" req placeholder="örn: Standard 10 Kullanıcı" />
              <FieldSelect form={form} set={set} k="edition" label="Edisyon" options={EDITION_OPTIONS} />
              <FieldText form={form} set={set} k="maxUsers" label="Maks. Kullanıcı" num />
              <FieldText form={form} set={set} k="durationDays" label="Süre (gün) — boş = süresiz" num />
              <FieldSelect form={form} set={set} k="isActive" label="Durum" options={ACTIVE_OPTIONS} />
            </Grid>
          </Section>
        </div>
      )}

      {tab === 'menus' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Menü Kapsamı"
            subtitle="Bu lisansın kapsadığı menüleri seçin; lisans atanan tenant yalnız bu menüleri kullanabilir"
            right={(
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="dps-btn ghost" onClick={selectAll}>Tümünü Seç</button>
                <button type="button" className="dps-btn ghost" onClick={clearAll}>Temizle</button>
              </div>
            )}
          >
            {menusLoading ? (
              <div className="dps-loading">Menüler yükleniyor…</div>
            ) : menuTree.length === 0 ? (
              <p style={{ margin: 0, opacity: 0.7 }}>Menü bulunamadı.</p>
            ) : (
              <>
                {(() => {
                  // Feature = menü ana başlığı. Özet: tam/kısmi açık ana başlıkları chip olarak göster.
                  const active = menuTree.map((n) => ({ label: n.label, st: nodeState(n) })).filter((x) => x.st !== 'off');
                  return (
                    <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: 'var(--surface-2, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 6 }}>
                        Aktif Feature'lar (Ana Başlıklar) — {active.length}/{menuTree.length}
                      </div>
                      {active.length === 0 ? (
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Henüz feature seçilmedi.</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {active.map((x) => (
                            <span key={x.label} style={{
                              display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                              background: x.st === 'on' ? '#eef2ff' : '#fffbeb',
                              color: x.st === 'on' ? '#4338ca' : '#92400e',
                              border: '1px solid ' + (x.st === 'on' ? '#c7d2fe' : '#fde68a'),
                            }} title={x.st === 'on' ? 'Tüm alt menüler dahil' : 'Kısmen seçili'}>
                              {x.label}{x.st === 'partial' ? ' •' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div style={{ maxHeight: 560, overflow: 'auto', paddingRight: 4 }}>
                  {menuTree.map((n) => renderNode(n))}
                </div>
              </>
            )}
          </Section>
        </div>
      )}

      {deleteConfirm && (
        <SubModal
          title="Silme Onayı" maxWidth={440} onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.name}”</strong> lisansını silmek istediğinize emin misiniz? Bir tenant'a atanmışsa silinemez.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default LicenseDetailPage;
